import { useState } from "react";

import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/ui/Spinner";
import { PageHeader, SectionCard } from "@/features/power_projects/components/ui";
import { formatDate } from "@/utils/formatDate";

import {
  useDirectives,
  useRuns,
  useSuggestionComment,
  useSuggestionFeedback,
  useSuggestionPromote,
  useSuggestions,
  useTriggerRun,
  useUpdateDirectives,
} from "../hooks";
import type { FinPriority, FinRun, FinSuggestion } from "../types";

const PRIORITY_CLS: Record<FinPriority, string> = {
  urgent: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  high: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  normal: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300",
  low: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
};

const PRIORITY_LABEL: Record<FinPriority, string> = {
  urgent: "PILNE", high: "WYSOKIE", normal: "ZWYKŁE", low: "NISKIE",
};

const RUN_STATUS: Record<string, { label: string; cls: string }> = {
  pending: { label: "W kolejce", cls: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300" },
  running: { label: "W trakcie", cls: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300" },
  ok: { label: "Gotowe", cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" },
  nothing_todo: { label: "Nic nie trzeba", cls: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300" },
  skipped_nochange: { label: "Pominięto — bez zmian", cls: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300" },
  skipped_budget: { label: "Pominięto — budżet", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" },
  error: { label: "Błąd", cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" },
};

/**
 * Run history. The endpoint has existed since the module shipped; the page only
 * ever showed the newest run, so a doradca failing or being cut off by the
 * budget looked exactly like one that had nothing to say.
 */
function RunHistoryCard() {
  const { data: runs, isLoading } = useRuns("portfolio");
  const [open, setOpen] = useState(false);

  if (isLoading) return null;
  if (!runs || runs.length === 0) return null;

  const shown = open ? runs : runs.slice(0, 5);
  const totalCost = runs.reduce((sum, r) => sum + Number(r.cost_usd || 0), 0);
  const failed = runs.filter((r) => r.status === "error" || r.status === "skipped_budget").length;

  return (
    <SectionCard title="Historia przebiegów" className="mb-4">
      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        <span className="text-gray-500">
          Ostatnie {runs.length}{" "}
          {runs.length === 1 ? "uruchomienie" : "uruchomień"}
        </span>
        <span className="tabular-nums">
          Koszt łącznie: <strong>${totalCost.toFixed(3)}</strong>
        </span>
        {failed > 0 && (
          <span className="text-red-700 dark:text-red-400">
            Nieudanych lub obciętych budżetem: <strong>{failed}</strong>
          </span>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-gray-400">
              <th className="py-1.5 pr-3">Data</th>
              <th className="py-1.5 pr-3">Status</th>
              <th className="py-1.5 pr-3 text-right">Koszt</th>
              <th className="py-1.5 pr-3 text-right">Tokeny we/wy</th>
              <th className="py-1.5">Wynik</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r: FinRun) => {
              const st = RUN_STATUS[r.status] ?? { label: r.status, cls: "bg-gray-100 text-gray-600" };
              return (
                <tr key={r.id} className="border-t border-gray-100 dark:border-gray-800 align-top">
                  <td className="py-1.5 pr-3 whitespace-nowrap tabular-nums">{formatDate(r.run_date)}</td>
                  <td className="py-1.5 pr-3">
                    <span className={`inline-block px-1.5 py-0.5 text-xs font-medium ${st.cls}`}>
                      {st.label}
                    </span>
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">
                    ${Number(r.cost_usd || 0).toFixed(3)}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-gray-500">
                    {r.input_tokens.toLocaleString("pl-PL")} / {r.output_tokens.toLocaleString("pl-PL")}
                  </td>
                  <td className="py-1.5 text-gray-600 dark:text-gray-300">
                    {r.executive_line || r.reason || "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {runs.length > 5 && (
        <button
          onClick={() => setOpen((v) => !v)}
          className="mt-2 text-sm text-[var(--color-primary)] hover:underline"
        >
          {open ? "Pokaż mniej" : `Pokaż wszystkie (${runs.length})`}
        </button>
      )}
    </SectionCard>
  );
}

function SuggestionCard({ s }: { s: FinSuggestion }) {
  const feedback = useSuggestionFeedback();
  const comment = useSuggestionComment();
  const promote = useSuggestionPromote();
  const [commentText, setCommentText] = useState("");

  return (
    <div className="border border-[var(--color-border)] rounded-lg p-3 mb-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <span className={`inline-block text-[10px] font-bold rounded px-1.5 py-0.5 mr-2 ${PRIORITY_CLS[s.priority]}`}>
            {PRIORITY_LABEL[s.priority]}
          </span>
          <span className="font-medium">{s.title}</span>
          {s.data_limited && (
            <span className="ml-2 text-[10px] text-amber-600">dane niepełne</span>
          )}
        </div>
        <span className="text-xs text-gray-400 whitespace-nowrap">
          {s.age_days != null ? `${s.age_days} dni` : ""}
        </span>
      </div>
      <p className="text-sm mt-1 whitespace-pre-wrap">{s.body_md}</p>
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <Button variant="secondary" onClick={() => feedback.mutate({ id: s.id, status: "done" })}>
          Zrobione
        </Button>
        <Button variant="secondary" onClick={() => feedback.mutate({ id: s.id, status: "dismissed" })}>
          Odrzuć
        </Button>
        <Button variant="secondary" onClick={() => promote.mutate(s.id)}>
          → Baza wiedzy FIN
        </Button>
        <input
          value={commentText}
          onChange={(e) => setCommentText(e.target.value)}
          placeholder="Komentarz…"
          className="border border-[var(--color-border)] rounded px-2 py-1 text-sm flex-1 min-w-40 bg-transparent"
          onKeyDown={(e) => {
            if (e.key === "Enter" && commentText.trim()) {
              comment.mutate({ id: s.id, body: commentText.trim() });
              setCommentText("");
            }
          }}
        />
      </div>
      {(s.events?.filter((e) => e.kind === "commented").length ?? 0) > 0 && (
        <div className="mt-2 space-y-1">
          {s.events!.filter((e) => e.kind === "commented").map((e, i) => (
            <p key={i} className="text-xs text-gray-500">
              <span className="font-medium">{e.actor ?? "—"}:</span> {e.body}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

export default function FinAronAdvisorPage() {
  const { data, isLoading } = useSuggestions("portfolio");
  const trigger = useTriggerRun();
  const directives = useDirectives();
  const updateDirectives = useUpdateDirectives();
  const [directiveText, setDirectiveText] = useState<string | null>(null);

  if (isLoading || !data) return <PageLoader />;
  const run = data.run;

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      <PageHeader
        title="FIN-ARON — rekomendacje AI"
        subtitle={
          run
            ? `Ostatni przebieg: ${formatDate(run.run_date)} (${run.status}) · koszt $${Number(run.cost_usd).toFixed(3)}`
            : "Brak przebiegów — uruchom analizę."
        }
        backTo="/fin-aron"
        backLabel="Koordynatorzy × marże"
        actions={
          <Button onClick={() => trigger.mutate({ scope: "portfolio" })} disabled={trigger.isPending}>
            {trigger.isPending ? "Uruchamianie…" : "Uruchom analizę"}
          </Button>
        }
      />

      {trigger.isSuccess && (
        <p className="text-sm text-emerald-600 mb-3">
          Analiza w toku — wróć za chwilę i odśwież.
        </p>
      )}

      {run?.executive_line && (
        <SectionCard title="Podsumowanie dla kierownictwa" className="mb-4">
          <p className="text-sm font-medium">{run.executive_line}</p>
          {run.nothing_to_do && <p className="text-sm text-gray-500 mt-1">{run.reason}</p>}
        </SectionCard>
      )}

      {(run?.leaks?.length ?? 0) > 0 && (
        <SectionCard title="Przecieki wykryte deterministycznie" className="mb-4">
          <ul className="text-sm space-y-1">
            {run!.leaks!.map((l, i) => (
              <li key={i} className="text-red-700 dark:text-red-400">• [{l.kind}] {l.detail}</li>
            ))}
          </ul>
        </SectionCard>
      )}

      <RunHistoryCard />

      <SectionCard title={`Otwarte rekomendacje (${data.suggestions.length})`} className="mb-4">
        {data.suggestions.length === 0 && (
          <p className="text-sm text-gray-400">Brak otwartych rekomendacji.</p>
        )}
        {data.suggestions.map((s) => (
          <SuggestionCard key={s.id} s={s} />
        ))}
      </SectionCard>

      <SectionCard title="Ustalenia kierownictwa (nadrzędne dla AI)" className="mb-4">
        {directives.data?.is_editor ? (
          <div>
            <textarea
              value={directiveText ?? directives.data?.text ?? ""}
              onChange={(e) => setDirectiveText(e.target.value)}
              rows={3}
              className="w-full border border-[var(--color-border)] rounded px-2 py-1.5 text-sm bg-transparent"
              placeholder="np. Priorytet: marże projektów ENERGA; nie ruszać obłożenia zespołu X."
            />
            <Button
              className="mt-2"
              onClick={() => updateDirectives.mutate(directiveText ?? "")}
              disabled={updateDirectives.isPending || directiveText === null}
            >
              Zapisz ustalenia
            </Button>
          </div>
        ) : (
          <p className="text-sm whitespace-pre-wrap">
            {directives.data?.text || "Brak ustaleń."}
          </p>
        )}
      </SectionCard>

      {data.history.length > 0 && (
        <SectionCard title="Historia">
          <table className="w-full text-sm">
            <tbody>
              {data.history.map((s) => (
                <tr key={s.id} className="border-b border-[var(--color-border)]">
                  <td className="py-1.5 pr-3">{s.title}</td>
                  <td className="py-1.5 pr-3 text-xs text-gray-500">{s.status}</td>
                  <td className="py-1.5 text-xs text-gray-400 text-right">
                    {formatDate(s.resolved_at ?? s.raised_at, "")}
                    {s.resolution_verified && " ✓"}
                    {s.reopened_count > 0 && ` (wracała ×${s.reopened_count})`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </SectionCard>
      )}
    </div>
  );
}
